import type { ChannelSummary, UpdateChannelRequest } from '@pulsify/shared'
import { EVENT_TRANSLATION_CREDIT_COST } from '@pulsify/shared/dto'
import { Loader2, Pencil, Plus, Sparkles } from 'lucide-react'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useSaveOrganizerProfile } from '~/entities/channel/model/use-organizer'
import { useTranslateDescription } from '~/features/ai-studio/hooks'
import {
  aboutPatch,
  HANDLE_MAX,
  isValidHandle,
  linksPatch,
  namePatch,
} from '~/features/organizer/lib/organizer-profile'
import { organizerPageHost } from '~/features/organizer/lib/public-page'
import { CreditCostTag } from '~/features/shared/components/credit-cost'
import { ClampedContent } from '~/features/shared/components/view/clamped-content'
import { ContactLinks, hasContactLinks } from '~/features/shared/components/view/contact-links-view'
import { DetailList, DetailRow } from '~/features/shared/components/view/detail-row'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { richTextToPlainText, toRichTextHtml } from '~/shared/lib/rich-text'
import { NetworkError } from '~/shared/lib/rpc'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { Textarea } from '~/shared/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/shared/ui/tooltip'

type Section = 'name' | 'about' | 'links'

type FormProps = { businessId: string; channel: ChannelSummary; onDone: () => void }

/** Name, about text and links of an organizer page: read here, one part edited at a time. */
export function OrganizerDetailsCard({
  businessId,
  channel,
}: {
  businessId: string
  channel: ChannelSummary
}) {
  const [open, setOpen] = useState<Section | null>(null)
  const locked = open !== null
  const form = { businessId, channel, onDone: () => setOpen(null) }
  const row = (section: Section, label: string) => ({
    label,
    editing: open === section,
    locked,
    onEdit: () => setOpen(section),
  })

  return (
    <Card className="p-4 sm:p-6">
      <h2 className="mb-4 font-semibold font-serif text-lg">{m.organizerDetails()}</h2>
      <DetailList>
        <SectionRow {...row('name', m.organizerSectionName())} form={<NameForm {...form} />}>
          <div className="space-y-0.5">
            <p className="break-words font-medium">{channel.name}</p>
            <p className="break-all text-muted-foreground">@{channel.handle}</p>
          </div>
        </SectionRow>
        <SectionRow {...row('about', m.organizerAbout())} form={<AboutForm {...form} />}>
          <AboutView
            channel={channel}
            empty={
              <EmptyPrompt
                text={m.organizerAboutEmpty()}
                action={m.organizerAboutAdd()}
                disabled={locked}
                onAction={() => setOpen('about')}
              />
            }
          />
        </SectionRow>
        <SectionRow {...row('links', m.organizerLinks())} form={<LinksForm {...form} />}>
          <LinksView
            channel={channel}
            empty={
              <EmptyPrompt
                text={m.organizerLinksEmpty()}
                action={m.organizerLinksAdd()}
                disabled={locked}
                onAction={() => setOpen('links')}
              />
            }
          />
        </SectionRow>
      </DetailList>
    </Card>
  )
}

function SectionRow({
  label,
  editing,
  locked,
  onEdit,
  form,
  children,
}: {
  label: string
  editing: boolean
  locked: boolean
  onEdit: () => void
  form: ReactNode
  children: ReactNode
}) {
  return (
    <DetailRow
      label={label}
      action={editing ? null : <EditButton label={label} disabled={locked} onClick={onEdit} />}
    >
      {editing ? form : children}
    </DetailRow>
  )
}

function EditButton({
  label,
  disabled,
  onClick,
}: {
  label: string
  disabled: boolean
  onClick: () => void
}) {
  const text = `${m.edit()}: ${label}`
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={text}
          disabled={disabled}
          onClick={onClick}
        >
          <Pencil />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  )
}

function EmptyPrompt({
  text,
  action,
  disabled,
  onAction,
}: {
  text: string
  action: string
  disabled: boolean
  onAction: () => void
}) {
  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-muted-foreground">{text}</p>
      <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onAction}>
        <Plus />
        {action}
      </Button>
    </div>
  )
}

function AboutView({ channel, empty }: { channel: ChannelSummary; empty: ReactNode }) {
  const { description, descriptionEn } = channel
  if (!description && !descriptionEn) return empty
  return (
    <div className="space-y-3">
      {description ? (
        <ClampedContent className="max-w-prose whitespace-pre-wrap leading-relaxed">
          {description}
        </ClampedContent>
      ) : null}
      {descriptionEn ? (
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">{m.organizerAboutEnglish()}</p>
          <ClampedContent className="max-w-prose whitespace-pre-wrap leading-relaxed">
            <span lang="en">{descriptionEn}</span>
          </ClampedContent>
        </div>
      ) : (
        <p className="text-muted-foreground text-xs">{m.organizerAboutEnglishMissing()}</p>
      )}
    </div>
  )
}

function LinksView({ channel, empty }: { channel: ChannelSummary; empty: ReactNode }) {
  const links = {
    website: channel.website ?? undefined,
    instagram: channel.instagram ?? undefined,
  }
  return hasContactLinks(links) ? <ContactLinks {...links} /> : empty
}

function useSectionSave(businessId: string, channelId: string, onDone: () => void) {
  const save = useSaveOrganizerProfile(businessId)
  const submit = (patch: UpdateChannelRequest, onHandleTaken?: () => void) =>
    save.mutate(
      { id: channelId, patch },
      {
        onSuccess: () => {
          toast.success(m.organizerSaved())
          onDone()
        },
        onError: (error) => {
          if (error instanceof NetworkError && error.code === 'ChannelHandleTaken') {
            onHandleTaken?.()
            toast.error(m.organizerHandleTaken())
            return
          }
          toast.error(m.organizerSaveFailed())
        },
      },
    )
  return { submit, pending: save.isPending }
}

function useFocusOnMount<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  useEffect(() => {
    ref.current?.focus()
  }, [])
  return ref
}

function SectionForm({
  busy,
  ready,
  onCancel,
  onSubmit,
  children,
}: {
  busy: boolean
  ready: boolean
  onCancel: () => void
  onSubmit: () => void
  children: ReactNode
}) {
  return (
    <form
      className="@container space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (ready && !busy) onSubmit()
      }}
    >
      {children}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
          {m.cancel()}
        </Button>
        <Button type="submit" disabled={!ready || busy}>
          {busy ? <Loader2 className="motion-safe:animate-spin" /> : null}
          {m.organizerSave()}
        </Button>
      </div>
    </form>
  )
}

function NameForm({ businessId, channel, onDone }: FormProps) {
  const [name, setName] = useState(channel.name)
  const [handle, setHandle] = useState(channel.handle)
  const [takenHandle, setTakenHandle] = useState<string | null>(null)
  const nameId = useId()
  const handleId = useId()
  const nameRef = useFocusOnMount<HTMLInputElement>()
  const save = useSectionSave(businessId, channel.id, onDone)
  const handleError = isValidHandle(handle)
    ? takenHandle === handle
      ? m.organizerHandleTaken()
      : null
    : m.organizerHandleInvalid()

  return (
    <SectionForm
      busy={save.pending}
      ready={name.trim().length > 0 && !handleError}
      onCancel={onDone}
      onSubmit={() => save.submit(namePatch(channel, name, handle), () => setTakenHandle(handle))}
    >
      <div className="grid gap-4 @lg:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={nameId}>{m.organizerName()}</Label>
          <Input
            ref={nameRef}
            id={nameId}
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={handleId}>{m.organizerHandle()}</Label>
          <Input
            id={handleId}
            value={handle}
            maxLength={HANDLE_MAX}
            autoCapitalize="none"
            spellCheck={false}
            aria-invalid={handleError ? true : undefined}
            aria-describedby={`${handleId}-hint`}
            onChange={(e) => setHandle(e.target.value.toLowerCase())}
          />
          <p id={`${handleId}-hint`} className="break-all text-muted-foreground text-xs">
            {organizerPageHost(handle)}
          </p>
          {handleError ? <p className="text-destructive text-xs">{handleError}</p> : null}
        </div>
      </div>
    </SectionForm>
  )
}

function AboutForm({ businessId, channel, onDone }: FormProps) {
  const [description, setDescription] = useState(channel.description ?? '')
  const [descriptionEn, setDescriptionEn] = useState(channel.descriptionEn ?? '')
  const germanId = useId()
  const englishId = useId()
  const germanRef = useFocusOnMount<HTMLTextAreaElement>()
  const translate = useTranslateDescription(businessId)
  const save = useSectionSave(businessId, channel.id, onDone)

  const createEnglish = () => {
    if (!description.trim()) {
      toast.error(m.eventDescriptionTranslateNeedsText())
      return
    }
    translate.mutate(
      { html: toRichTextHtml(description.trim()), from: 'de', to: 'en' },
      {
        onSuccess: ({ html }) => setDescriptionEn(richTextToPlainText(html).slice(0, 500)),
        onError: (error) => toast.error(getErrorMessageFromError(error)),
      },
    )
  }

  return (
    <SectionForm
      busy={save.pending}
      ready={!translate.isPending}
      onCancel={onDone}
      onSubmit={() => save.submit(aboutPatch(description, descriptionEn))}
    >
      <div className="space-y-1.5">
        <Label htmlFor={germanId}>{m.organizerAboutGerman()}</Label>
        <Textarea
          ref={germanRef}
          id={germanId}
          value={description}
          maxLength={500}
          rows={4}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <div className="flex min-h-8 flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <Label htmlFor={englishId}>{m.organizerAboutEnglishOptional()}</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={translate.isPending}
            onClick={createEnglish}
          >
            {translate.isPending ? (
              <PulsifyLoader className="size-4" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {translate.isPending ? m.eventDescriptionTranslating() : m.organizerAboutEnAi()}
            <CreditCostTag cost={EVENT_TRANSLATION_CREDIT_COST} />
          </Button>
        </div>
        <Textarea
          id={englishId}
          value={descriptionEn}
          maxLength={500}
          rows={4}
          lang="en"
          onChange={(e) => setDescriptionEn(e.target.value)}
        />
      </div>
    </SectionForm>
  )
}

function LinksForm({ businessId, channel, onDone }: FormProps) {
  const [website, setWebsite] = useState(channel.website ?? '')
  const [instagram, setInstagram] = useState(channel.instagram ?? '')
  const websiteId = useId()
  const instagramId = useId()
  const websiteRef = useFocusOnMount<HTMLInputElement>()
  const save = useSectionSave(businessId, channel.id, onDone)

  return (
    <SectionForm
      busy={save.pending}
      ready
      onCancel={onDone}
      onSubmit={() => save.submit(linksPatch(website, instagram))}
    >
      <div className="grid gap-4 @lg:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={websiteId}>{m.organizerWebsite()}</Label>
          <Input
            ref={websiteRef}
            id={websiteId}
            value={website}
            inputMode="url"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="https://"
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={instagramId}>{m.instagram()}</Label>
          <Input
            id={instagramId}
            value={instagram}
            autoCapitalize="none"
            spellCheck={false}
            placeholder="@"
            onChange={(e) => setInstagram(e.target.value)}
          />
        </div>
      </div>
    </SectionForm>
  )
}
